import React, { useState, useEffect } from 'react';
import { ClockIcon, CheckIcon, XMarkIcon, FlagIcon } from '@heroicons/react/24/outline';
import { apiService } from '../services/apiService';
import { useUser } from '../contexts/UserContext';

const SystemSettings = () => {
  const { isAdmin } = useUser();
  const [timezoneSettings, setTimezoneSettings] = useState(null);
  const [availableTimezones, setAvailableTimezones] = useState([]);
  const [selectedTimezone, setSelectedTimezone] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [projectTrackerEnabled, setProjectTrackerEnabled] = useState(false);
  const [togglingProjectTracker, setTogglingProjectTracker] = useState(false);
  const [defenderXdrSyncEnabled, setDefenderXdrSyncEnabled] = useState(false);
  const [togglingDefenderXdrSync, setTogglingDefenderXdrSync] = useState(false);
  const [syncingDefenderXdr, setSyncingDefenderXdr] = useState(false);
  const [defenderXdrSyncResult, setDefenderXdrSyncResult] = useState(null);

  useEffect(() => {
    if (isAdmin()) {
      loadTimezoneSettings();
      apiService.isProjectTrackerEnabled().then(setProjectTrackerEnabled).catch(() => {});
      apiService.isDefenderXdrSyncEnabled().then(setDefenderXdrSyncEnabled).catch(() => {});
    }
  }, [isAdmin]);

  const loadTimezoneSettings = async () => {
    try {
      setLoading(true);
      const [timezoneData, timezonesData] = await Promise.all([
        apiService.makeRequest('/api/systemsettings/timezone'),
        apiService.makeRequest('/api/systemsettings/timezones')
      ]);

      setTimezoneSettings(timezoneData);
      setSelectedTimezone(timezoneData.timezoneId);
      setAvailableTimezones(timezonesData);
    } catch (error) {
      console.error('Failed to load timezone settings:', error);
      setMessage({ type: 'error', text: 'Failed to load timezone settings' });
    } finally {
      setLoading(false);
    }
  };

  const handleTimezoneChange = async (e) => {
    e.preventDefault();
    if (!selectedTimezone) return;

    try {
      setSaving(true);
      setMessage({ type: '', text: '' });

      const result = await apiService.makeRequest('/api/systemsettings/timezone', {
        method: 'PUT',
        body: JSON.stringify(selectedTimezone)
      });

      setMessage({ type: 'success', text: result.message || 'Timezone updated successfully' });
      // Reload settings to show updated values
      await loadTimezoneSettings();
    } catch (error) {
      console.error('Failed to update timezone:', error);
      
      // Try to get more detailed error information
      let errorMessage = 'Failed to update timezone';
      if (error.message) {
        errorMessage = error.message;
      }
      
      // If it's a fetch error, try to get the response body
      if (error.response) {
        try {
          const errorData = await error.response.json();
          if (errorData.message) {
            errorMessage = errorData.message;
          }
          if (errorData.error) {
            errorMessage += `: ${errorData.error}`;
          }
        } catch (parseError) {
          console.error('Failed to parse error response:', parseError);
        }
      }
      
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleProjectTracker = async () => {
    setTogglingProjectTracker(true);
    try {
      const newValue = (!projectTrackerEnabled).toString();
      await apiService.makeRequest('/api/systemsettings/feature.project_tracker', {
        method: 'PUT',
        body: JSON.stringify(newValue),
      });
      setProjectTrackerEnabled(!projectTrackerEnabled);
    } catch {
      setMessage({ type: 'error', text: 'Failed to update feature flag' });
    } finally {
      setTogglingProjectTracker(false);
    }
  };

  const handleToggleDefenderXdrSync = async () => {
    setTogglingDefenderXdrSync(true);
    try {
      const newValue = (!defenderXdrSyncEnabled).toString();
      await apiService.makeRequest('/api/systemsettings/feature.defender_xdr_sync', {
        method: 'PUT',
        body: JSON.stringify(newValue),
      });
      setDefenderXdrSyncEnabled(!defenderXdrSyncEnabled);
    } catch {
      setMessage({ type: 'error', text: 'Failed to update feature flag' });
    } finally {
      setTogglingDefenderXdrSync(false);
    }
  };

  const handleSyncDefenderXdrNow = async () => {
    setSyncingDefenderXdr(true);
    setDefenderXdrSyncResult(null);
    try {
      const result = await apiService.makeRequest('/api/systemsettings/defender-xdr/sync-now', {
        method: 'POST',
      });
      setDefenderXdrSyncResult({
        type: result.lastRunSucceeded ? 'success' : 'error',
        text: result.message,
      });
    } catch {
      setDefenderXdrSyncResult({ type: 'error', text: 'Failed to trigger Defender XDR sync' });
    } finally {
      setSyncingDefenderXdr(false);
    }
  };

  if (!isAdmin()) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <XMarkIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Access Denied</h3>
              <p className="text-sm text-red-700 mt-1">
                You need administrator privileges to access system settings.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">System Settings</h1>
        <p className="text-gray-600 mt-1">Configure system-wide settings and preferences</p>
      </div>

      {message.text && (
        <div className={`mb-6 p-4 rounded-lg ${
          message.type === 'success' 
            ? 'bg-green-50 border border-green-200' 
            : 'bg-red-50 border border-red-200'
        }`}>
          <div className="flex">
            {message.type === 'success' ? (
              <CheckIcon className="h-5 w-5 text-green-400" />
            ) : (
              <XMarkIcon className="h-5 w-5 text-red-400" />
            )}
            <div className="ml-3">
              <p className={`text-sm font-medium ${
                message.type === 'success' ? 'text-green-800' : 'text-red-800'
              }`}>
                {message.text}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Timezone Settings */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center mb-4">
          <div className="bg-blue-500 p-3 rounded-lg">
            <ClockIcon className="h-6 w-6 text-white" />
          </div>
          <div className="ml-4">
            <h2 className="text-lg font-medium text-gray-900">Timezone Settings</h2>
            <p className="text-sm text-gray-600">Configure the system timezone for date and time display</p>
          </div>
        </div>

        {timezoneSettings && (
          <div className="mb-4 p-4 bg-gray-50 rounded-lg">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Current Settings</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-700">Timezone:</span>
                <span className="ml-2 text-gray-600">{timezoneSettings.timezoneId}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Display Name:</span>
                <span className="ml-2 text-gray-600">{timezoneSettings.displayName}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">UTC Offset:</span>
                <span className="ml-2 text-gray-600">{timezoneSettings.offset}</span>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleTimezoneChange}>
          <div className="mb-4">
            <label htmlFor="timezone" className="block text-sm font-medium text-gray-700 mb-2">
              Select Timezone
            </label>
            <select
              id="timezone"
              value={selectedTimezone}
              onChange={(e) => setSelectedTimezone(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              disabled={saving}
            >
              <option value="">Select a timezone...</option>
              {availableTimezones.map((tz) => (
                <option key={tz.id} value={tz.id}>
                  {tz.name} ({tz.offset})
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              <p>Recommended: <strong>South Africa Standard Time (UTC+02:00)</strong></p>
              <p>This will fix the 2-hour time difference issue.</p>
            </div>
            <button
              type="submit"
              disabled={!selectedTimezone || saving}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : 'Update Timezone'}
            </button>
          </div>
        </form>
      </div>

      {/* Feature Flags */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center mb-5">
          <div className="bg-violet-500 p-3 rounded-lg">
            <FlagIcon className="h-6 w-6 text-white" />
          </div>
          <div className="ml-4">
            <h2 className="text-lg font-medium text-gray-900">Feature Flags</h2>
            <p className="text-sm text-gray-600">Enable or disable optional modules without a deployment</p>
          </div>
        </div>
        <div className="divide-y divide-gray-100">
          <div className="flex items-center justify-between py-4">
            <div>
              <div className="text-sm font-medium text-gray-900">IT Project Tracker</div>
              <div className="text-xs text-gray-500 mt-0.5">
                Kanban &amp; waterfall project management with phases, budget tracking, and ticket linking
              </div>
            </div>
            <button
              onClick={handleToggleProjectTracker}
              disabled={togglingProjectTracker}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 ${
                projectTrackerEnabled ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                projectTrackerEnabled ? 'translate-x-5' : 'translate-x-0'
              }`} />
            </button>
          </div>
          <div className="flex items-center justify-between py-4">
            <div>
              <div className="text-sm font-medium text-gray-900">Defender XDR Incident Sync</div>
              <div className="text-xs text-gray-500 mt-0.5">
                Automatically create tickets from Microsoft 365 Defender XDR security incidents
              </div>
            </div>
            <button
              onClick={handleToggleDefenderXdrSync}
              disabled={togglingDefenderXdrSync}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 ${
                defenderXdrSyncEnabled ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                defenderXdrSyncEnabled ? 'translate-x-5' : 'translate-x-0'
              }`} />
            </button>
          </div>
          {defenderXdrSyncEnabled && (
            <div className="py-4">
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-500">
                  Run a sync immediately instead of waiting for the next scheduled poll.
                </p>
                <button
                  onClick={handleSyncDefenderXdrNow}
                  disabled={syncingDefenderXdr}
                  className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {syncingDefenderXdr ? 'Syncing...' : 'Sync Now'}
                </button>
              </div>
              {defenderXdrSyncResult && (
                <p className={`text-xs mt-2 ${
                  defenderXdrSyncResult.type === 'success' ? 'text-green-700' : 'text-red-700'
                }`}>
                  {defenderXdrSyncResult.text}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SystemSettings;
